import React, { FC, useState, useEffect } from 'react';
import { PageTitle } from '../../../rbts/layout/core';
import Select from 'react-select';
import { styleSelect } from '../../../rbts/assets/ts/_utils';
import { formatPrice } from '../../../rbts/helpers';
import { useObtenerDiasPorEvento } from '../reportes/cortesias/hooks/CortesiasHook';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useDashboard } from './DashboardHook';

// ─── Paleta de colores ──────────────────────────────────────────────────────
const C = {
  primary:  '#4A5DDC',
  purple:   '#7239EA',
  success:  '#50CD89',
  warning:  '#E67E00',
  danger:   '#F1416C',
  info:     '#009EF7',
  dark:     '#181C32',
  muted:    '#A1A5B7',
  border:   '#F0F3F9',
  cardBg:   '#FFFFFF',
};

const CORTESIAS_PAGE_SIZE = 10;

// ─── KPI Card ───────────────────────────────────────────────────────────────
interface KpiCardProps {
  title: string;
  value: number;
  subtitle: string;
  icon: string;
  color: string;
  prefix?: string;
  decimals?: number;
}
const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, icon, color, prefix = '$', decimals = 2 }) => (
  <div style={{
    background: C.cardBg,
    borderRadius: '16px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    padding: '22px 20px',
    borderTop: `4px solid ${color}`,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: C.muted, fontSize: '10px', fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
          {title}
        </p>
        <h2 style={{ fontSize: '24px', fontWeight: 800, color: C.dark, margin: 0, lineHeight: 1.2 }}>
          {prefix}<CountUp start={0} end={value} duration={1.3} separator="," decimals={decimals} />
        </h2>
        <p style={{ color: C.muted, fontSize: '11px', marginTop: 6, marginBottom: 0 }}>{subtitle}</p>
      </div>
      <div style={{
        width: '48px', height: '48px', borderRadius: '12px', flexShrink: 0, marginLeft: '12px',
        background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={`bi bi-${icon}`} style={{ color, fontSize: '22px' }} />
      </div>
    </div>
  </div>
);

// ─── Section Card ───────────────────────────────────────────────────────────
interface SectionCardProps {
  title: string;
  subtitle?: string;
  icon: string;
  children: React.ReactNode;
  noPad?: boolean;
}
const SectionCard: React.FC<SectionCardProps> = ({ title, subtitle, icon, children, noPad }) => (
  <div style={{
    background: C.cardBg,
    borderRadius: '16px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    marginBottom: '24px',
  }}>
    <div style={{
      background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
      padding: '14px 22px',
      display: 'flex', alignItems: 'center', gap: '12px',
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        background: 'rgba(255,255,255,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <i className={`bi bi-${icon}`} style={{ color: 'white', fontSize: '17px' }} />
      </div>
      <div>
        <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: 700 }}>{title}</h3>
        {subtitle && <p style={{ color: 'rgba(255,255,255,0.72)', margin: 0, fontSize: '11px' }}>{subtitle}</p>}
      </div>
    </div>
    <div style={noPad ? {} : { padding: '22px' }}>{children}</div>
  </div>
);

// ─── Empty State ────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px', color: C.muted }}>
    <i className="bi bi-inbox" style={{ fontSize: '48px', display: 'block', marginBottom: 12, opacity: 0.5 }} />
    <p style={{ margin: 0, fontSize: '13px' }}>{message}</p>
  </div>
);

// ─── Dashboard Principal ────────────────────────────────────────────────────
const DashboardWrapper: FC = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [expandedPanel, setExpandedPanel] = useState<'caja' | 'puestos' | 'cortesias' | 'retiros' | null>(null);
  const [puestosColapsados, setPuestosColapsados] = useState<Set<string>>(new Set());
  const [cortesiaPage, setCortesiaPage] = useState(0);

  const togglePanel = (panel: 'caja' | 'puestos' | 'cortesias' | 'retiros') =>
    setExpandedPanel(prev => (prev === panel ? null : panel));

  const togglePuesto = (puesto: string) => {
    setPuestosColapsados(prev => {
      const next = new Set(prev);
      if (next.has(puesto)) next.delete(puesto); else next.add(puesto);
      return next;
    });
  };
  const { list: listaDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);

  useEffect(() => {
    if (listaDias.length > 0 && diasSeleccionados.length === 0) {
      setDiasSeleccionados([listaDias[listaDias.length - 1]]);
    }
  }, [listaDias]); // eslint-disable-line react-hooks/exhaustive-deps

  const idsDia = diasSeleccionados.map((d: any) => d.value as number);
  const labelPeriodo = idsDia.length === 1 ? 'del Día' : 'del Período';
  const { loading, kpi, remanenteKpi, montoLimite, ventasPorHora, ventasPorPV, topProductos, estadoCajas, conciliaciones, ventasPorCaja, ventasPorPuestos, cortesiasKpi, refresh } = useDashboard(idsDia);
  useEffect(() => { setCortesiaPage(0); }, [cortesiasKpi.detalle.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Opciones: Ventas por Hora ────────────────────────────────────────────
  const lineOptions: ApexOptions = {
    chart:      { type: 'area', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 600 } },
    dataLabels: { enabled: false },
    stroke:     { curve: 'smooth', width: 3 },
    colors:     [C.primary, C.purple],
    fill:       { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0.02, stops: [0, 100] } },
    xaxis: {
      categories: ventasPorHora.map(h => h.hora),
      labels:     { style: { colors: C.muted, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: {
      labels: {
        style:     { colors: C.muted, fontSize: '11px' },
        formatter: (v: number) => `$${v.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`,
      },
    },
    grid:    { borderColor: C.border, strokeDashArray: 4 },
    legend:  { position: 'top', horizontalAlign: 'right', labels: { colors: C.dark }, fontSize: '12px' },
    tooltip: { y: { formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 2, 2) } },
  };
  const lineSeries = [
    { name: 'Efectivo',    data: ventasPorHora.map(h => Math.round(h.efectivo    * 100) / 100) },
    { name: 'Electrónico', data: ventasPorHora.map(h => Math.round(h.electronico * 100) / 100) },
  ];

  // ── Opciones: Distribución de Pagos ─────────────────────────────────────
  const donutSeries = [
    Math.round(ventasPorCaja.total    * 100) / 100,
    Math.round(ventasPorPuestos.total * 100) / 100,
  ];
  const donutTotal = ventasPorCaja.total + ventasPorPuestos.total;
  const donutOptions: ApexOptions = {
    chart:      { type: 'donut', fontFamily: 'inherit' },
    labels:     ['Ventas por Caja', 'Ventas por Puestos'],
    colors:     [C.primary, C.warning],
    legend:     { position: 'bottom', labels: { colors: C.dark }, fontSize: '12px' },
    dataLabels: { enabled: true, formatter: (val: number) => `${val.toFixed(1)}%` },
    stroke:     { width: 2 },
    plotOptions: {
      pie: {
        donut: {
          size: '66%',
          labels: {
            show: true,
            total: {
              show:      true,
              label:     'Total',
              fontSize:  '13px',
              color:     C.muted,
              formatter: () => formatPrice(donutTotal, 'es-MX', 'MXN', 2, 2),
            },
          },
        },
      },
    },
    tooltip: { y: { formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 2, 2) } },
  };

  // ── Opciones: Top Productos (barra horizontal) ───────────────────────────
  const topReversed = [...topProductos].reverse();
  const barOptions: ApexOptions = {
    chart:      { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 600 } },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, dataLabels: { position: 'top' } } },
    colors:     [C.primary],
    fill:       { type: 'gradient', gradient: { gradientToColors: [C.purple], stops: [0, 100], type: 'horizontal' } },
    dataLabels: {
      enabled:   true,
      offsetX:   6,
      style:     { fontSize: '11px', colors: [C.dark], fontWeight: '700' },
      formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 0, 0),
    },
    xaxis: {
      categories: topReversed.map(p => p.nombre),
      labels:     { formatter: (v: string) => `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`, style: { colors: C.muted, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis:   { labels: { style: { colors: C.dark, fontSize: '12px', fontWeight: '600' } } },
    grid:    { borderColor: C.border, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 2, 2) } },
  };
  const barSeries = [{ name: 'Ventas', data: topReversed.map(p => Math.round(p.total * 100) / 100) }];

  const maxVentaPV = Math.max(...ventasPorPV.map(p => p.total), 1);

  // ── Totales globales de conciliación ─────────────────────────────────────
  const concilTotales = [
    { label: 'Total Electrónico',    value: conciliaciones.reduce((s, c) => s + c.total_electronico,   0), color: C.purple,  icon: 'currency-exchange' },
    { label: 'Total Banco',          value: conciliaciones.reduce((s, c) => s + c.total_banco,          0), color: C.info,    icon: 'bank2'            },
    { label: 'Total Punto de Venta', value: conciliaciones.reduce((s, c) => s + c.total_punto_venta,   0), color: C.primary, icon: 'shop-window'      },
    { label: 'Total Materia Prima',  value: conciliaciones.reduce((s, c) => s + c.total_materia_prima, 0), color: C.warning, icon: 'box2'             },
    { label: 'Total a Pagar',        value: conciliaciones.reduce((s, c) => s + c.total_a_pagar,       0), color: C.success, icon: 'check2-circle'    },
  ];

  return (
    <>
      <PageTitle breadcrumbs={[]}>Inicio</PageTitle>

      {/* ── Header con selector de día ──────────────────────────────── */}
      <div style={{
        background:     `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
        borderRadius:   '16px',
        padding:        '20px 26px',
        marginBottom:   '24px',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        flexWrap:       'wrap',
        gap:            '16px',
        boxShadow:      '0 8px 30px rgba(74,93,220,0.28)',
      }}>
        <div>
          <h2 style={{ color: 'white', margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-0.3px' }}>
            Dashboard General
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.72)', margin: 0, fontSize: '13px', marginTop: 4 }}>
            {idsDia.length <= 1
              ? 'Resumen operativo del día administrativo seleccionado'
              : `Resumen operativo de ${idsDia.length} días seleccionados`}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '320px' }}>
            <label style={{ color: 'rgba(255,255,255,0.82)', fontSize: '11px', fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              Días Administrativos
            </label>
            <Select
            value={diasSeleccionados}
            isLoading={cargandoDias}
            options={listaDias}
            styles={{
              ...styleSelect,
              control: (base: any) => ({ ...base, borderRadius: 10, minHeight: 42, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.18)' }),
            }}
            placeholder="Seleccione uno o varios días..."
            onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
            isMulti
            closeMenuOnSelect={false}
            isClearable
            noOptionsMessage={() => 'Sin opciones'}
            />
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            title="Actualizar dashboard"
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1.5px solid rgba(255,255,255,0.4)',
              borderRadius: '10px',
              color: 'white',
              padding: '10px 16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: 600,
              opacity: loading ? 0.6 : 1,
              whiteSpace: 'nowrap',
              height: '42px',
            }}
          >
            {loading
              ? <span className="spinner-border spinner-border-sm" role="status" />
              : <i className="bi bi-arrow-clockwise" style={{ fontSize: '16px' }} />
            }
            Actualizar
          </button>
        </div>
      </div>

      {/* ── Loading ─────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: C.primary }} />
          <p style={{ marginTop: 16, color: C.muted, fontWeight: 600, fontSize: '14px' }}>Cargando dashboard...</p>
        </div>
      )}

      {/* ── Sin día seleccionado ─────────────────────────────────────── */}
      {!loading && idsDia.length === 0 && (
        <div style={{ background: C.cardBg, borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', padding: '70px 24px', textAlign: 'center' }}>
          <i className="bi bi-calendar3" style={{ fontSize: '60px', color: C.primary, display: 'block', marginBottom: 16, opacity: 0.4 }} />
          <h3 style={{ color: C.dark, fontWeight: 700, marginBottom: 8, fontSize: '18px' }}>Selecciona un Día Administrativo</h3>
          <p style={{ color: C.muted, fontSize: '14px', margin: 0 }}>
            Elige un día en el selector superior para visualizar el dashboard completo.
          </p>
        </div>
      )}

      {!loading && idsDia.length > 0 && (
        <>
          {/* ── SECCIÓN 1: KPI Cards ──────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '20px', marginBottom: '16px' }}>
            {/* Ventas por Caja — card expandible */}
            <div
              onClick={() => togglePanel('caja')}
              style={{
                background: expandedPanel === 'caja' ? `${C.primary}0C` : C.cardBg,
                borderRadius: '16px',
                boxShadow: expandedPanel === 'caja' ? `0 4px 20px rgba(74,93,220,0.22)` : '0 2px 16px rgba(0,0,0,0.07)',
                padding: '22px 20px',
                borderTop: `4px solid ${C.primary}`,
                outline: expandedPanel === 'caja' ? `2px solid ${C.primary}` : '2px solid transparent',
                outlineOffset: '-1px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'background 0.2s, box-shadow 0.2s, outline-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.muted, fontSize: '10px', fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Ventas por Caja
                  </p>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: C.dark, margin: 0, lineHeight: 1.2 }}>
                    $<CountUp start={0} end={ventasPorCaja.total} duration={1.3} separator="," decimals={2} />
                  </h2>
                  <p style={{ color: C.muted, fontSize: '11px', marginTop: 6, marginBottom: 0 }}>
                    {ventasPorCaja.detalle.length} punto{ventasPorCaja.detalle.length !== 1 ? 's' : ''} de venta
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: `${C.primary}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-receipt" style={{ color: C.primary, fontSize: '22px' }} />
                  </div>
                  <i className={`bi bi-chevron-${expandedPanel === 'caja' ? 'up' : 'down'}`} style={{ color: C.primary, fontSize: '13px' }} />
                </div>
              </div>
            </div>

            {/* Ventas por Puestos — card expandible */}
            <div
              onClick={() => togglePanel('puestos')}
              style={{
                background: expandedPanel === 'puestos' ? `${C.primary}0C` : C.cardBg,
                borderRadius: '16px',
                boxShadow: expandedPanel === 'puestos' ? `0 4px 20px rgba(74,93,220,0.22)` : '0 2px 16px rgba(0,0,0,0.07)',
                padding: '22px 20px',
                borderTop: `4px solid ${C.primary}`,
                outline: expandedPanel === 'puestos' ? `2px solid ${C.primary}` : '2px solid transparent',
                outlineOffset: '-1px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'background 0.2s, box-shadow 0.2s, outline-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.muted, fontSize: '10px', fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Ventas por Puestos
                  </p>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: C.dark, margin: 0, lineHeight: 1.2 }}>
                    $<CountUp start={0} end={ventasPorPuestos.total} duration={1.3} separator="," decimals={2} />
                  </h2>
                  <p style={{ color: C.muted, fontSize: '11px', marginTop: 6, marginBottom: 0 }}>
                    {new Set(ventasPorPuestos.detalle.map(d => d.puntoventa)).size} puesto{new Set(ventasPorPuestos.detalle.map(d => d.puntoventa)).size !== 1 ? 's' : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: `${C.primary}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-shop" style={{ color: C.primary, fontSize: '22px' }} />
                  </div>
                  <i className={`bi bi-chevron-${expandedPanel === 'puestos' ? 'up' : 'down'}`} style={{ color: C.primary, fontSize: '13px' }} />
                </div>
              </div>
            </div>

            {/* Cortesías — card expandible */}
            <div
              onClick={() => togglePanel('cortesias')}
              style={{
                background: expandedPanel === 'cortesias' ? `${C.primary}0C` : C.cardBg,
                borderRadius: '16px',
                boxShadow: expandedPanel === 'cortesias' ? `0 4px 20px rgba(74,93,220,0.22)` : '0 2px 16px rgba(0,0,0,0.07)',
                padding: '22px 20px',
                borderTop: `4px solid ${C.primary}`,
                outline: expandedPanel === 'cortesias' ? `2px solid ${C.primary}` : '2px solid transparent',
                outlineOffset: '-1px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'background 0.2s, box-shadow 0.2s, outline-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.muted, fontSize: '10px', fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Cortesías
                  </p>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: C.dark, margin: 0, lineHeight: 1.2 }}>
                    $<CountUp start={0} end={cortesiasKpi.total} duration={1.3} separator="," decimals={2} />
                  </h2>
                  <p style={{ color: C.muted, fontSize: '11px', marginTop: 6, marginBottom: 0 }}>
                    {cortesiasKpi.detalle.length} ticket{cortesiasKpi.detalle.length !== 1 ? 's' : ''} con cortesía
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: `${C.primary}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-gift" style={{ color: C.primary, fontSize: '22px' }} />
                  </div>
                  <i className={`bi bi-chevron-${expandedPanel === 'cortesias' ? 'up' : 'down'}`} style={{ color: C.primary, fontSize: '13px' }} />
                </div>
              </div>
            </div>

            {/* Retiros — card expandible */}
            <div
              onClick={() => togglePanel('retiros')}
              style={{
                background: expandedPanel === 'retiros' ? `${C.primary}0C` : C.cardBg,
                borderRadius: '16px',
                boxShadow: expandedPanel === 'retiros' ? `0 4px 20px rgba(74,93,220,0.22)` : '0 2px 16px rgba(0,0,0,0.07)',
                padding: '22px 20px',
                borderTop: `4px solid ${C.primary}`,
                outline: expandedPanel === 'retiros' ? `2px solid ${C.primary}` : '2px solid transparent',
                outlineOffset: '-1px',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'background 0.2s, box-shadow 0.2s, outline-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: C.muted, fontSize: '10px', fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Retiros
                  </p>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: C.dark, margin: 0, lineHeight: 1.2 }}>
                    $<CountUp start={0} end={estadoCajas.reduce((s, c) => s + c.monto_retirado, 0)} duration={1.3} separator="," decimals={2} />
                  </h2>
                  <p style={{ color: C.muted, fontSize: '11px', marginTop: 6, marginBottom: 0 }}>
                    {estadoCajas.length} caja{estadoCajas.length !== 1 ? 's' : ''} registrada{estadoCajas.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: '12px' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: `${C.primary}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-arrow-up-circle" style={{ color: C.primary, fontSize: '22px' }} />
                  </div>
                  <i className={`bi bi-chevron-${expandedPanel === 'retiros' ? 'up' : 'down'}`} style={{ color: C.primary, fontSize: '13px' }} />
                </div>
              </div>
            </div>

            {/* <KpiCard
              title="Ventas en Efectivo"
              value={kpi.totalEfectivo}
              subtitle={kpi.totalVentas > 0 ? `${((kpi.totalEfectivo / kpi.totalVentas) * 100).toFixed(1)}% del total` : '—'}
              icon="cash-stack"
              color={C.success}
            />
            <KpiCard
              title="Ventas Electrónicas"
              value={kpi.totalElectronico}
              subtitle={kpi.totalVentas > 0 ? `${((kpi.totalElectronico / kpi.totalVentas) * 100).toFixed(1)}% del total` : '—'}
              icon="credit-card-2-front"
              color={C.purple}
            /> */}
            {/* <KpiCard
              title="Total Retirado"
              value={kpi.totalRetirado}
              subtitle={`${kpi.totalCajas} caja${kpi.totalCajas !== 1 ? 's' : ''} registradas`}
              icon="arrow-up-circle"
              color={C.warning}
            />
            <KpiCard
              title="Por Conciliar"
              value={kpi.totalPorConciliar}
              subtitle={`${kpi.totalConciliaciones} conciliación${kpi.totalConciliaciones !== 1 ? 'es' : ''}`}
              icon="clipboard-check"
              color={C.info}
            /> */}
          </div>

          {/* ── Panel detalle Ventas por Caja ────────────────────── */}
          {expandedPanel === 'caja' && (
            <div style={{ background: C.cardBg, borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', marginBottom: '24px', overflow: 'hidden' }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
                padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-receipt" style={{ color: 'white', fontSize: '17px' }} />
                </div>
                <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: 700 }}>Detalle de Ventas por Caja</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                {ventasPorCaja.detalle.length > 0 ? (() => {
                  const totBraEfe = ventasPorCaja.detalle.reduce((s, r) => s + r.brazaletes_efe, 0);
                  const totBraTar = ventasPorCaja.detalle.reduce((s, r) => s + r.brazaletes_tar, 0);
                  const totTcEfe  = ventasPorCaja.detalle.reduce((s, r) => s + r.ticketcash_efe,  0);
                  const totTcTar  = ventasPorCaja.detalle.reduce((s, r) => s + r.ticketcash_tar,  0);
                  const thGroup  = { padding: '10px 16px', textAlign: 'center' as const, color: C.dark,  fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: `1px solid ${C.border}` };
                  const thSub    = { padding: '6px 12px',  textAlign: 'right'  as const, color: C.muted, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.4px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' as const };
                  const tdVal    = { padding: '11px 12px', textAlign: 'right'  as const, color: C.dark,  fontSize: '13px' };
                  const tdFoot   = { padding: '11px 12px', textAlign: 'right'  as const, fontWeight: 800, color: C.dark, fontSize: '13px' };
                  const fmt      = (v: number) => v > 0 ? formatPrice(v, 'es-MX', 'MXN', 2, 2) : '—';
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th rowSpan={2} style={{ ...thGroup, textAlign: 'left', borderBottom: `2px solid ${C.border}`, verticalAlign: 'bottom' }}>Punto de Venta</th>
                          <th colSpan={2} style={{ ...thGroup, borderLeft: `1px solid ${C.border}` }}>Brazaletes</th>
                          <th colSpan={2} style={{ ...thGroup, borderLeft: `1px solid ${C.border}` }}>TicketCash</th>
                          <th rowSpan={2} style={{ ...thGroup, textAlign: 'right', borderLeft: `1px solid ${C.border}`, borderBottom: `2px solid ${C.border}`, verticalAlign: 'bottom', color: C.primary }}>Total</th>
                        </tr>
                        <tr>
                          <th style={{ ...thSub, borderLeft: `1px solid ${C.border}` }}>Efectivo</th>
                          <th style={thSub}>Tarjeta</th>
                          <th style={{ ...thSub, borderLeft: `1px solid ${C.border}` }}>Efectivo</th>
                          <th style={thSub}>Tarjeta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ventasPorCaja.detalle.map(row => (
                          <tr key={row.puntoventa} style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: '11px 16px', fontWeight: 600, color: C.dark, fontSize: '13px' }}>{row.puntoventa}</td>
                            <td style={{ ...tdVal, borderLeft: `1px solid ${C.border}` }}>{fmt(row.brazaletes_efe)}</td>
                            <td style={tdVal}>{fmt(row.brazaletes_tar)}</td>
                            <td style={{ ...tdVal, borderLeft: `1px solid ${C.border}` }}>{fmt(row.ticketcash_efe)}</td>
                            <td style={tdVal}>{fmt(row.ticketcash_tar)}</td>
                            <td style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '14px', borderLeft: `1px solid ${C.border}` }}>
                              {formatPrice(row.total, 'es-MX', 'MXN', 2, 2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: `2px solid ${C.border}`, background: `${C.primary}08` }}>
                          <td style={{ padding: '11px 16px', fontWeight: 800, color: C.dark, fontSize: '13px' }}>Total</td>
                          <td style={{ ...tdFoot, borderLeft: `1px solid ${C.border}` }}>{fmt(totBraEfe)}</td>
                          <td style={tdFoot}>{fmt(totBraTar)}</td>
                          <td style={{ ...tdFoot, borderLeft: `1px solid ${C.border}` }}>{fmt(totTcEfe)}</td>
                          <td style={tdFoot}>{fmt(totTcTar)}</td>
                          <td style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px', borderLeft: `1px solid ${C.border}` }}>
                            {formatPrice(ventasPorCaja.total, 'es-MX', 'MXN', 2, 2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })() : (
                  <EmptyState message="No hay ventas de caja para este día" />
                )}
              </div>
            </div>
          )}

          {/* ── Panel detalle Ventas por Puestos ─────────────── */}
          {expandedPanel === 'puestos' && (
            <div style={{ background: C.cardBg, borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', marginBottom: '24px', overflow: 'hidden' }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
                padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-shop" style={{ color: 'white', fontSize: '17px' }} />
                </div>
                <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: 700 }}>Detalle de Ventas por Puestos</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                {ventasPorPuestos.detalle.length > 0 ? (() => {
                  // Agrupar por puesto
                  const grupos: Record<string, typeof ventasPorPuestos.detalle> = {};
                  ventasPorPuestos.detalle.forEach(row => {
                    if (!grupos[row.puntoventa]) grupos[row.puntoventa] = [];
                    grupos[row.puntoventa].push(row);
                  });
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                          {['Punto de Venta / Producto', 'Cantidad', 'Total'].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Punto de Venta / Producto' ? 'left' : 'right', color: C.muted, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(grupos).map(([puesto, productos]) => {
                          const subtotalCantidad = productos.reduce((s, r) => s + r.cantidad, 0);
                          const subtotalTotal    = productos.reduce((s, r) => s + r.total, 0);
                          const colapsado        = puestosColapsados.has(puesto);
                          return (
                            <React.Fragment key={puesto}>
                              {/* Fila de cabecera del puesto — clickeable */}
                              <tr
                                onClick={() => togglePuesto(puesto)}
                                style={{ background: `${C.primary}15`, borderTop: `2px solid ${C.border}`, cursor: 'pointer', userSelect: 'none' }}
                              >
                                <td style={{ padding: '10px 16px', fontWeight: 800, color: C.dark, fontSize: '13px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <i className={`bi bi-chevron-${colapsado ? 'right' : 'down'}`} style={{ color: C.primary, fontSize: '11px', flexShrink: 0 }} />
                                  {puesto}
                                </td>
                                <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: C.dark, fontSize: '13px' }}>{subtotalCantidad}</td>
                                <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '13px' }}>
                                  {formatPrice(subtotalTotal, 'es-MX', 'MXN', 2, 2)}
                                </td>
                              </tr>
                              {/* Filas de productos — visibles solo si no está colapsado */}
                              {!colapsado && productos.map((row, i) => (
                                <tr key={`${puesto}-${row.producto}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                                  <td style={{ padding: '9px 16px 9px 36px', color: C.muted, fontSize: '12px' }}>{row.producto}</td>
                                  <td style={{ padding: '9px 16px', textAlign: 'right', color: C.dark, fontSize: '12px', fontWeight: 600 }}>{row.cantidad}</td>
                                  <td style={{ padding: '9px 16px', textAlign: 'right', color: C.dark, fontSize: '12px' }}>
                                    {formatPrice(row.total, 'es-MX', 'MXN', 2, 2)}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: `2px solid ${C.border}`, background: `${C.primary}08` }}>
                          <td style={{ padding: '12px 16px', fontWeight: 800, color: C.dark, fontSize: '13px' }}>Total</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.dark, fontSize: '13px' }}>
                            {ventasPorPuestos.detalle.reduce((s, r) => s + r.cantidad, 0)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px' }}>
                            {formatPrice(ventasPorPuestos.total, 'es-MX', 'MXN', 2, 2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })() : (
                  <EmptyState message="No hay ventas de puestos para este día" />
                )}
              </div>
            </div>
          )}

          {/* ── Panel detalle Cortesías ──────────────────────────── */}
          {expandedPanel === 'cortesias' && (
            <div style={{ background: C.cardBg, borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', marginBottom: '24px', overflow: 'hidden' }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
                padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-gift" style={{ color: 'white', fontSize: '17px' }} />
                </div>
                <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: 700 }}>Detalle de Cortesías</h3>
              </div>

              <div style={{ overflowX: 'auto' }}>
                {cortesiasKpi.detalle.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                        {['Folio', 'Cliente', 'Monto Cortesía', 'Consumido', 'Recarga', 'Transferido', 'Balance'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', textAlign: h === 'Folio' || h === 'Cliente' ? 'left' : 'right', color: h === 'Transferido' ? C.primary : C.muted, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cortesiasKpi.detalle.slice(cortesiaPage * CORTESIAS_PAGE_SIZE, (cortesiaPage + 1) * CORTESIAS_PAGE_SIZE).map((row, i) => (
                        <tr key={`${row.folio}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '12px 16px', fontWeight: 700, color: C.dark, fontSize: '13px' }}>{row.folio}</td>
                          <td style={{ padding: '12px 16px', color: C.dark, fontSize: '13px' }}>{row.cliente}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: C.primary, fontSize: '13px' }}>
                            {formatPrice(row.totalCortesia, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: C.dark, fontSize: '13px' }}>
                            {row.totalConsumido > 0 ? formatPrice(row.totalConsumido, 'es-MX', 'MXN', 2, 2) : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: C.dark, fontSize: '13px' }}>
                            {row.recarga > 0 ? formatPrice(row.recarga, 'es-MX', 'MXN', 2, 2) : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, fontSize: '13px', color: row.transferido > 0 ? C.primary : row.transferido < 0 ? C.danger : C.muted }}>
                            {row.transferido > 0
                              ? formatPrice(row.transferido, 'es-MX', 'MXN', 2, 2)
                              : row.transferido < 0
                                ? `-${formatPrice(Math.abs(row.transferido), 'es-MX', 'MXN', 2, 2)}`
                                : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, fontSize: '13px', color: row.balance >= 0 ? C.primary : C.danger }}>
                            {formatPrice(row.balance, 'es-MX', 'MXN', 2, 2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${C.border}`, background: `${C.primary}08` }}>
                        <td style={{ padding: '12px 16px', fontWeight: 800, color: C.dark, fontSize: '13px' }} colSpan={2}>Total</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px' }}>
                          {formatPrice(cortesiasKpi.total, 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.dark, fontSize: '13px' }}>
                          {formatPrice(cortesiasKpi.detalle.reduce((s, r) => s + r.totalConsumido, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.dark, fontSize: '13px' }}>
                          {formatPrice(cortesiasKpi.detalle.reduce((s, r) => s + r.recarga, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.muted, fontSize: '13px' }}>—</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px' }}>
                          {formatPrice(cortesiasKpi.detalle.reduce((s, r) => s + r.balance, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <EmptyState message="No hay cortesías registradas" />
                )}
                {cortesiasKpi.detalle.length > CORTESIAS_PAGE_SIZE && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: `1px solid ${C.border}` }}>
                    <span style={{ color: C.muted, fontSize: '12px' }}>
                      Página {cortesiaPage + 1} de {Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE)} — {cortesiasKpi.detalle.length} registros
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setCortesiaPage(p => Math.max(0, p - 1))}
                        disabled={cortesiaPage === 0}
                        style={{
                          padding: '4px 14px', borderRadius: '6px', border: `1px solid ${C.border}`,
                          background: cortesiaPage === 0 ? C.border : 'white',
                          color: cortesiaPage === 0 ? C.muted : C.primary,
                          cursor: cortesiaPage === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '12px', fontWeight: 600,
                        }}
                      >← Anterior</button>
                      <button
                        onClick={() => setCortesiaPage(p => Math.min(Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE) - 1, p + 1))}
                        disabled={cortesiaPage >= Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE) - 1}
                        style={{
                          padding: '4px 14px', borderRadius: '6px', border: `1px solid ${C.border}`,
                          background: cortesiaPage >= Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE) - 1 ? C.border : C.primary,
                          color: cortesiaPage >= Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE) - 1 ? C.muted : 'white',
                          cursor: cortesiaPage >= Math.ceil(cortesiasKpi.detalle.length / CORTESIAS_PAGE_SIZE) - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '12px', fontWeight: 600,
                        }}
                      >Siguiente →</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Panel detalle Retiros ───────────────────────────── */}
          {expandedPanel === 'retiros' && (
            <div style={{ background: C.cardBg, borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', marginBottom: '24px', overflow: 'hidden' }}>
              <div style={{
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
                padding: '14px 22px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-arrow-up-circle" style={{ color: 'white', fontSize: '17px' }} />
                </div>
                <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: 700 }}>Detalle de Retiros</h3>
              </div>

              <div style={{ overflowX: 'auto' }}>
                {estadoCajas.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                        {['Caja', 'Monto Inicial', 'Venta Acumulada', 'Monto Retirado', 'Total Real', 'Alerta de Retiro'].map(h => (
                          <th key={h} style={{
                            padding: '12px 16px',
                            textAlign: h === 'Caja' ? 'left' : 'right',
                            color: C.muted, fontSize: '10px', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap',
                          }}>
                            {h === 'Alerta de Retiro'
                              ? <span style={{ display: 'block', textAlign: 'center' }}>{h}</span>
                              : h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {estadoCajas.map((caja, i) => {
                        const negativo = caja.total_real < 0;
                        const sobre    = !negativo && montoLimite > 0 && caja.total_real > montoLimite;
                        const cerca    = !negativo && !sobre && montoLimite > 0 && caja.total_real > montoLimite * 0.75;
                        const alertColor = negativo || sobre ? C.danger : cerca ? C.warning : C.success;
                        const alertLabel = negativo ? 'Saldo negativo' : sobre ? 'Requiere retiro' : cerca ? 'Cerca del límite' : 'OK';
                        const alertIcon  = negativo || sobre ? 'exclamation-triangle-fill' : cerca ? 'exclamation-circle-fill' : 'check-square-fill';
                        return (
                          <tr key={caja.caja} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'white' : `${C.primary}04` }}>
                            <td style={{ padding: '12px 16px', fontWeight: 700, color: C.dark, fontSize: '13px' }}>{caja.caja}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: C.dark, fontSize: '13px' }}>
                              {formatPrice(caja.inicio, 'es-MX', 'MXN', 2, 2)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: C.primary, fontWeight: 700, fontSize: '13px' }}>
                              {formatPrice(caja.venta_acumulada, 'es-MX', 'MXN', 2, 2)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: C.dark, fontSize: '13px' }}>
                              {formatPrice(caja.monto_retirado, 'es-MX', 'MXN', 2, 2)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: C.primary, fontSize: '13px' }}>
                              {formatPrice(caja.total_real, 'es-MX', 'MXN', 2, 2)}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: `${alertColor}18`, color: alertColor,
                                borderRadius: '20px', padding: '4px 10px',
                                fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
                              }}>
                                <i className={`bi bi-${alertIcon}`} style={{ fontSize: '11px' }} />
                                {alertLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${C.border}`, background: `${C.primary}08` }}>
                        <td style={{ padding: '12px 16px', fontWeight: 800, color: C.dark, fontSize: '13px' }}>Total</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.dark, fontSize: '13px' }}>
                          {formatPrice(estadoCajas.reduce((s, c) => s + c.inicio, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px' }}>
                          {formatPrice(estadoCajas.reduce((s, c) => s + c.venta_acumulada, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.dark, fontSize: '13px' }}>
                          {formatPrice(estadoCajas.reduce((s, c) => s + c.monto_retirado, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: C.primary, fontSize: '15px' }}>
                          {formatPrice(estadoCajas.reduce((s, c) => s + c.total_real, 0), 'es-MX', 'MXN', 2, 2)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <EmptyState message="No hay información de retiros para este día" />
                )}
              </div>
            </div>
          )}

          {/* ── SECCIÓN 2: Gráficas — ocultas temporalmente ──────── */}
          {/* <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', marginBottom: '24px' }}>
            <SectionCard title="Distribución de Ventas" subtitle="Cajas · Puestos" icon="pie-chart">
              {donutTotal > 0
                ? <ReactApexChart type="donut" series={donutSeries} options={donutOptions} height={290} />
                : <EmptyState message="Sin datos de distribución para este día" />}
            </SectionCard>

            <SectionCard title="Cortesías" subtitle="Monto asignado vs consumido" icon="gift">
              {cortesiasKpi.total > 0 ? (() => {
                const totalConsumido = cortesiasKpi.detalle.reduce((s, d) => s + d.totalConsumido, 0);
                return (
                  <ReactApexChart
                    type="bar"
                    height={290}
                    series={[{ name: 'Monto', data: [Math.round(cortesiasKpi.total * 100) / 100, Math.round(totalConsumido * 100) / 100] }]}
                    options={{
                      chart:       { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 600 } },
                      plotOptions: { bar: { horizontal: false, borderRadius: 8, columnWidth: '45%', distributed: true } },
                      colors:      [C.success, C.primary],
                      legend:      { show: false },
                      dataLabels: {
                        enabled:   true,
                        style:     { fontSize: '12px', colors: [C.dark], fontWeight: '700' },
                        formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 0, 0),
                      },
                      xaxis: {
                        categories: ['Monto Cortesía', 'Consumido'],
                        labels:     { style: { colors: C.dark, fontSize: '13px', fontWeight: '600' } },
                        axisBorder: { show: false },
                        axisTicks:  { show: false },
                      },
                      yaxis: {
                        labels: {
                          formatter: (v: number) => `$${v.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`,
                          style:     { colors: C.muted, fontSize: '11px' },
                        },
                      },
                      grid:    { borderColor: C.border, strokeDashArray: 4 },
                      tooltip: { y: { formatter: (v: number) => formatPrice(v, 'es-MX', 'MXN', 2, 2) } },
                    } as ApexOptions}
                  />
                );
              })() : <EmptyState message="Sin cortesías registradas para este día" />}
            </SectionCard>
          </div> */}

          {/* ── SECCIÓN 2b: KPIs Remanente de Tickets ───────────── */}
          {/* <SectionCard title="Remanente de Tickets" subtitle="Tickets con saldo pendiente en los días seleccionados" icon="receipt-cutoff">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '16px' }}>
              <KpiCard
                title="Tickets con Remanente"
                value={remanenteKpi.totalTickets}
                subtitle={`${labelPeriodo}`}
                icon="receipt-cutoff"
                color={C.info}
                prefix=""
                decimals={0}
              />
              <KpiCard
                title="Total Cortesías"
                value={remanenteKpi.totalCortesias}
                subtitle="Monto cargado por cortesía"
                icon="gift"
                color={C.success}
              />
              <KpiCard
                title="Saldo Remanente"
                value={remanenteKpi.totalRemanente}
                subtitle="Saldo pendiente sin gastar"
                icon="wallet2"
                color={C.danger}
              />
            </div>
          </SectionCard> */}

          {false && (<>
          {/* ── SECCIÓN 3: Ventas por Punto de Venta ────────────── */}
          <SectionCard title="Ventas por Punto de Venta" subtitle="Ranking por monto total del día" icon="shop" noPad>
            {ventasPorPV.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {['#', 'Punto de Venta', 'Tipo', 'Total Ventas', 'Tickets', 'Producto Estrella', 'Participación'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: C.muted, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ventasPorPV.map((pv, i) => {
                      const pct = (pv.total / maxVentaPV) * 100;
                      return (
                        <tr
                          key={pv.nombre}
                          style={{ borderBottom: `1px solid ${C.border}`, transition: 'background 0.15s', cursor: 'default' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#F7F9FF'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                        >
                          <td style={{ padding: '14px 16px', fontSize: '16px' }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : (
                              <span style={{ color: C.muted, fontWeight: 700, fontSize: '13px' }}>#{i + 1}</span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px', fontWeight: 700, color: C.dark, fontSize: '13px', whiteSpace: 'nowrap' }}>{pv.nombre}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ background: `${C.info}18`, color: C.info, borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {pv.tipo || 'General'}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', fontWeight: 800, color: C.dark, fontSize: '14px', whiteSpace: 'nowrap' }}>
                            {formatPrice(pv.total, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td style={{ padding: '14px 16px', color: C.dark, fontWeight: 600, fontSize: '13px' }}>{pv.tickets}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ color: C.dark, fontSize: '12px', fontWeight: 600 }}>{pv.topProducto}</span>
                            {pv.topCantidad > 0 && (
                              <span style={{ color: C.muted, fontSize: '11px', marginLeft: 5 }}>×{pv.topCantidad}</span>
                            )}
                          </td>
                          <td style={{ padding: '14px 16px', minWidth: '130px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: '7px', background: C.border, borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${C.primary}, ${C.purple})`, borderRadius: '4px' }} />
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: C.muted, minWidth: '34px' }}>{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '22px' }}>
                <EmptyState message="No hay datos de ventas por punto de venta para este día" />
              </div>
            )}
          </SectionCard>

          {/* ── SECCIÓN 4: Top 10 Productos ──────────────────────── */}
          <SectionCard title="Top 10 Productos" subtitle="Productos con mayor ingreso en el día" icon="star">
            {topProductos.length > 0 ? (
              <ReactApexChart
                type="bar"
                series={barSeries}
                options={barOptions}
                height={Math.max(300, topReversed.length * 44)}
              />
            ) : (
              <EmptyState message="No hay datos de productos para este día" />
            )}
          </SectionCard>

          {/* ── SECCIÓN 5: Estado de Cajas ────────────────────────── */}
          <SectionCard title="Estado de Cajas" subtitle="Resumen operativo por caja / punto de venta" icon="safe2">
            {estadoCajas.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
                {estadoCajas.map(caja => {
                  const pctRetiro   = (caja.inicio + caja.venta_acumulada) > 0
                    ? (caja.monto_retirado / (caja.inicio + caja.venta_acumulada)) * 100 : 0;
                  const negativo    = caja.total_real < 0;
                  const sobre       = !negativo && montoLimite > 0 && caja.total_real > montoLimite;
                  const cerca       = !negativo && !sobre && montoLimite > 0 && caja.total_real > montoLimite * 0.75;
                  const statusColor = negativo ? C.danger : sobre ? C.danger : cerca ? C.warning : C.success;
                  const statusLabel = negativo ? 'Saldo negativo' : sobre ? 'Requiere retiro' : cerca ? 'Próximo al límite' : 'OK';
                  const statusIcon  = negativo ? 'x-circle-fill' : sobre ? 'exclamation-triangle-fill' : cerca ? 'exclamation-circle-fill' : 'check-circle-fill';

                  return (
                    <div key={caja.caja} style={{
                      border: `1px solid ${C.border}`, borderRadius: '14px', padding: '20px',
                      borderTop: `3px solid ${statusColor}`, background: C.cardBg,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h4 style={{ fontWeight: 700, color: C.dark, margin: 0, fontSize: '14px' }}>{caja.caja}</h4>
                        <span style={{ color: statusColor, fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <i className={`bi bi-${statusIcon}`} style={{ fontSize: '12px' }} />
                          {statusLabel}
                        </span>
                      </div>
                      {[
                        { label: 'Capital Inicial',    value: caja.inicio,          color: C.muted     },
                        { label: 'Venta Acumulada',    value: caja.venta_acumulada, color: C.success   },
                        { label: 'Total Retirado',     value: caja.monto_retirado,  color: C.warning   },
                        { label: 'Saldo Real en Caja', value: caja.total_real,      color: statusColor },
                      ].map(row => (
                        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                          <span style={{ color: C.muted, fontSize: '11px' }}>{row.label}</span>
                          <span style={{ color: row.color, fontSize: '12px', fontWeight: 700 }}>
                            {formatPrice(row.value, 'es-MX', 'MXN', 2, 2)}
                          </span>
                        </div>
                      ))}
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '10px', color: C.muted }}>Retiros vs capital disponible</span>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: C.muted }}>{pctRetiro > 100 ? '>100%' : `${pctRetiro.toFixed(1)}%`}</span>
                        </div>
                        <div style={{ height: '6px', background: C.border, borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(pctRetiro, 100)}%`, height: '100%', background: `linear-gradient(90deg, ${C.success}, ${statusColor})`, borderRadius: '4px', transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState message="No hay información de cajas para este día" />
            )}
          </SectionCard>

          {/* ── SECCIÓN 6: Resumen de Conciliación ───────────────── */}
          <SectionCard title="Resumen de Conciliación" subtitle="Estado y distribución de conciliaciones del día" icon="bank">
            {conciliaciones.length > 0 ? (
              <>
                {/* Totales globales */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                  {concilTotales.map(item => (
                    <div key={item.label} style={{
                      background: `${item.color}0A`, border: `1px solid ${item.color}22`,
                      borderRadius: '12px', padding: '16px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                        <i className={`bi bi-${item.icon}`} style={{ color: item.color, fontSize: '15px' }} />
                        <span style={{ color: C.muted, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</span>
                      </div>
                      <p style={{ color: item.color, fontSize: '18px', fontWeight: 800, margin: 0 }}>
                        {formatPrice(item.value, 'es-MX', 'MXN', 2, 2)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Detalle por conciliación */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {conciliaciones.map((c, i) => (
                    <div key={c.folio + i} style={{
                      border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px',
                      borderLeft: `4px solid ${C.primary}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 6 }}>
                        <h5 style={{ margin: 0, fontWeight: 700, color: C.dark, fontSize: '13px' }}>{c.folio}</h5>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <span style={{ background: `${C.info}18`, color: C.info, borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                            Banco {c.split_banco}%
                          </span>
                          <span style={{ background: `${C.primary}18`, color: C.primary, borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                            PV {c.split_punto_venta}%
                          </span>
                        </div>
                      </div>
                      {[
                        { label: 'Saldo Electrónico', value: c.total_electronico   },
                        { label: 'Corresponde Banco', value: c.total_banco         },
                        { label: 'Corresponde PV',    value: c.total_punto_venta   },
                        { label: 'Materia Prima',     value: c.total_materia_prima },
                      ].map(row => (
                        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ color: C.muted, fontSize: '11px' }}>{row.label}</span>
                          <span style={{ color: C.dark, fontSize: '12px', fontWeight: 600 }}>{formatPrice(row.value, 'es-MX', 'MXN', 2, 2)}</span>
                        </div>
                      ))}
                      <div style={{ borderTop: `2px solid ${C.border}`, marginTop: 10, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: C.dark, fontWeight: 700, fontSize: '12px' }}>Total a Pagar</span>
                        <span style={{ color: C.success, fontWeight: 800, fontSize: '16px' }}>{formatPrice(c.total_a_pagar, 'es-MX', 'MXN', 2, 2)}</span>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <div style={{ height: '8px', background: C.border, borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${c.split_banco}%`, background: C.info, transition: 'width 0.6s ease' }} />
                          <div style={{ width: `${c.split_punto_venta}%`, background: C.primary, transition: 'width 0.6s ease' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                          <span style={{ fontSize: '10px', color: C.info, fontWeight: 600 }}>Banco {c.split_banco}%</span>
                          <span style={{ fontSize: '10px', color: C.primary, fontWeight: 600 }}>PV {c.split_punto_venta}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState message="No hay conciliaciones registradas para este día" />
            )}
          </SectionCard>
          </>)}
        </>
      )}
    </>
  );
};

export { DashboardWrapper };
